import { ref, type Ref } from "vue";
import * as Cesium from "cesium";

interface ClickedCoordinate {
    longitude: number;
    latitude: number;
    height: number;
    cartesian: {
        x: number;
        y: number;
        z: number;
    };
    source: "3dtiles" | "ellipsoid";
}

export function useCoordinatePicker(viewer: Ref<Cesium.Viewer | null>) {
    const isCoordinatePickerActive = ref(false);
    const lastClickedCoordinate = ref<ClickedCoordinate | null>(null);

    const onLeftClick = (
        event: Cesium.ScreenSpaceEventHandler.PositionedEvent
    ): void => {
        if (!viewer.value || !isCoordinatePickerActive.value) return;

        // First try to pick from 3D tiles using pickPosition (most reliable for 3D tiles)
        const pickedPosition3D = viewer.value.scene.pickPosition(event.position);
        if (pickedPosition3D) {
            console.log("Picked position from 3D tiles using pickPosition");

            const cartographic = Cesium.Cartographic.fromCartesian(pickedPosition3D);
            const longitude = Cesium.Math.toDegrees(cartographic.longitude);
            const latitude = Cesium.Math.toDegrees(cartographic.latitude);
            const height = cartographic.height;

            lastClickedCoordinate.value = {
                longitude,
                latitude,
                height,
                cartesian: {
                    x: pickedPosition3D.x,
                    y: pickedPosition3D.y,
                    z: pickedPosition3D.z,
                },
                source: "3dtiles",
            };

            console.log("3D Tile coordinate captured:", {
                longitude: longitude.toFixed(6),
                latitude: latitude.toFixed(6),
                height: height.toFixed(2),
                source: "3dtiles",
            });
            return;
        }

        // Alternative method: try picking object and extract position
        const pickedObject = viewer.value.scene.pick(event.position);
        if (pickedObject && pickedObject.primitive) {
            console.log("Picked object:", pickedObject.primitive);
            console.log("Picked object type:", pickedObject.primitive.constructor.name);

            // Try different methods to get position from 3D tiles
            let position = null;

            // Method 1: Direct position property
            if (pickedObject.position) {
                position = pickedObject.position;
                console.log("Using pickedObject.position");
            }
            // Method 2: Get position from the primitive's bounding sphere center
            else if (pickedObject.primitive.boundingSphere) {
                position = pickedObject.primitive.boundingSphere.center;
                console.log("Using bounding sphere center");
            }
            // Method 3: Try to get position from the primitive's model matrix
            else if (pickedObject.primitive.modelMatrix) {
                position = Cesium.Matrix4.getTranslation(
                    pickedObject.primitive.modelMatrix,
                    new Cesium.Cartesian3()
                );
                console.log("Using model matrix translation");
            }

            if (position) {
                const cartographic = Cesium.Cartographic.fromCartesian(position);
                const longitude = Cesium.Math.toDegrees(cartographic.longitude);
                const latitude = Cesium.Math.toDegrees(cartographic.latitude);
                const height = cartographic.height;

                lastClickedCoordinate.value = {
                    longitude,
                    latitude,
                    height,
                    cartesian: {
                        x: position.x,
                        y: position.y,
                        z: position.z,
                    },
                    source: "3dtiles",
                };

                console.log("3D Tile coordinate captured (alternative method):", {
                    longitude: longitude.toFixed(6),
                    latitude: latitude.toFixed(6),
                    height: height.toFixed(2),
                    source: "3dtiles",
                });
                return;
            } else {
                console.log("Could not extract position from 3D tile primitive");
            }
        }

        // Fallback to ellipsoid picking
        const pickedPosition = viewer.value.camera.pickEllipsoid(
            event.position,
            viewer.value.scene.globe.ellipsoid
        );

        if (pickedPosition) {
            // Convert to geographic coordinates
            const cartographic = Cesium.Cartographic.fromCartesian(pickedPosition);
            const longitude = Cesium.Math.toDegrees(cartographic.longitude);
            const latitude = Cesium.Math.toDegrees(cartographic.latitude);
            const height = cartographic.height;

            // Store the coordinate information
            lastClickedCoordinate.value = {
                longitude,
                latitude,
                height,
                cartesian: {
                    x: pickedPosition.x,
                    y: pickedPosition.y,
                    z: pickedPosition.z,
                },
                source: "ellipsoid",
            };

            console.log("Ellipsoid coordinate captured:", {
                longitude: longitude.toFixed(6),
                latitude: latitude.toFixed(6),
                height: height.toFixed(2),
                source: "ellipsoid",
            });
        } else {
            console.log("No valid position found at click location");
        }
    };

    const toggleCoordinatePicker = (): void => {
        if (!viewer.value) return;

        isCoordinatePickerActive.value = !isCoordinatePickerActive.value;

        if (isCoordinatePickerActive.value) {
            // Enable coordinate picking
            viewer.value.cesiumWidget.screenSpaceEventHandler.setInputAction(
                onLeftClick,
                Cesium.ScreenSpaceEventType.LEFT_CLICK
            );
            console.log(
                "Coordinate picker enabled - click on 3D tiles to get coordinates"
            );
        } else {
            // Disable coordinate picking
            viewer.value.cesiumWidget.screenSpaceEventHandler.removeInputAction(
                Cesium.ScreenSpaceEventType.LEFT_CLICK
            );
            lastClickedCoordinate.value = null;
            console.log("Coordinate picker disabled");
        }
    };

    return {
        isCoordinatePickerActive,
        lastClickedCoordinate,
        toggleCoordinatePicker,
    };
}